import { Body, Caption, EmptyState, LoadingState, VBadge, cn } from '@vrzl/ui';
import { FileText, Search } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { CRITERIA_SEARCH_EMPTY_TITLE, CriteriaSearch, useCriteriaSearch } from '@/domains/criteria';
import { useDocumentEvidence } from '@/domains/report';
import type { DocumentEvidenceItem } from '@/domains/report/api/useDocumentEvidence';

import { evidenceSelectionKey, type EvidenceSelection } from '../lib/evidenceSelection';

type Props = {
  eventId: string;
  docUrl: string | null | undefined;
  selected: EvidenceSelection | null;
  onSelect: (item: DocumentEvidenceItem) => void;
};

const getName = (item: DocumentEvidenceItem) => item.criterionName;
const getValues = (item: DocumentEvidenceItem) => [item.sentence];
const itemKey = (item: DocumentEvidenceItem) => evidenceSelectionKey(item.criterionId, item);

export function DocumentResultsPanel({ eventId, docUrl, selected, onSelect }: Props) {
  const { items, isPending, hasContext } = useDocumentEvidence(eventId, docUrl);
  const { query, setQuery, results } = useCriteriaSearch(items, { getName, getValues });
  const selectedRef = useRef<HTMLLIElement>(null);

  const selectedKey = selected ? evidenceSelectionKey(selected.criterionId, selected.evidence) : null;
  const isSelectedListed = selectedKey !== null && results.some((ev) => itemKey(ev) === selectedKey);

  // Het actieve resultaat in beeld houden, ook als het bij openen ver onderaan de lijst staat.
  // Loopt opnieuw zodra de lijst geladen is (isSelectedListed wordt dan true).
  useEffect(() => {
    if (!isSelectedListed) return;
    selectedRef.current?.scrollIntoView({ block: 'nearest' });
  }, [selectedKey, isSelectedListed]);

  if (isPending) {
    return <LoadingState />;
  }

  if (items.length === 0) {
    return (
      <div className="p-4">
        {hasContext ? (
          <EmptyState
            icon={FileText}
            title="Niets gevonden in dit document"
            description="De AI heeft in dit document geen informatie gevonden die past bij de criteria. Dit document is dus niet gebruikt om de zaak te beoordelen."
          />
        ) : (
          <EmptyState
            icon={FileText}
            title="Deze zaak is nog niet beoordeeld"
            description="De AI heeft deze zaak nog niet verwerkt. Daarom is nog niet te zien welke informatie uit dit document gebruikt wordt."
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-2 overflow-y-auto p-4">
      <CriteriaSearch value={query} onChange={setQuery} />
      <Caption tone="muted" className="px-1 uppercase tracking-wide">
        Resultaten ({results.length})
      </Caption>
      {results.length === 0 ? (
        <EmptyState icon={Search} title={CRITERIA_SEARCH_EMPTY_TITLE} className="max-w-md" />
      ) : (
        <ul className="flex flex-col gap-2">
          {results.map((ev) => {
            const key = itemKey(ev);
            const isSelected = key === selectedKey;
            return (
              <li key={key} ref={isSelected ? selectedRef : undefined}>
                <button
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => onSelect(ev)}
                  className={cn(
                    'w-full rounded-md border border-border bg-card px-3 py-2 text-left transition-colors hover:bg-muted',
                    isSelected && 'border-neutral-400 bg-neutral-100',
                  )}
                >
                  <Caption tone="subtle" className="mb-1 truncate">
                    {ev.criterionName}
                  </Caption>
                  {ev.sentence && (
                    <Body tone="muted" className="italic line-clamp-3 whitespace-pre-line">
                      {ev.sentence}
                    </Body>
                  )}
                  {ev.pages && ev.pages.length > 0 && (
                    <div className="mt-1.5">
                      <VBadge color="secondary">pag. {ev.pages.join(', ')}</VBadge>
                    </div>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
